import { useStore } from '@nanostores/react';
import { memo, useState } from 'react';
import { UnpublishProject } from './UnpublishProject';
import { SectionRow } from '~/components/dialogs/settings/SectionContainer';
import { Button } from '~/components/ui/Button';
import { Link } from '~/components/ui/Link';
import { UpgradePlanDialogs } from '~/components/ui/pricing/UpgradePlanDialogs';
import { userStore } from '~/lib/stores/user';

interface ProjectPublishedOnSubdomainProps {
  url: string;
  projectId: string;
  onOpenConnectDomain: VoidFunction;
  onOpenBuyDomain: VoidFunction;
}

export const ProjectPublishedOnSubdomain = memo(
  ({ url, onOpenConnectDomain, onOpenBuyDomain, projectId }: ProjectPublishedOnSubdomainProps) => {
    const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);
    const activeOrganization = useStore(userStore.getActiveOrganization());
    const isPaidUser = useStore(userStore.isPaidUser);

    const handleDomainAction = (action: 'buy' | 'connect') => {
      if (!isPaidUser) {
        setIsUpgradeModalOpen(true);
        return;
      }

      if (action === 'buy') {
        onOpenBuyDomain();
      } else if (action === 'connect') {
        onOpenConnectDomain();
      }
    };

    return (
      <>
        <SectionRow
          layout="vertical"
          innerClassName="flex flex-col gap-4"
          titleClassName="text-bolt-ds-textTertiary font-semibold mb-0"
          action={
            <>
              <p className="text-sm">
                Your project is currently accessible via the <b>bolt.host</b> subdomain. You can also allow visitors to
                access your project with your own custom domain, by purchasing a new one or connecting one you own.
              </p>
              <div className="flex flex-col gap-2">
                <h4 className="text-bolt-ds-textTertiary text-xs">Bolt subdomain</h4>
                <Link className="flex gap-2 decoration-none items-center" href={url} target="_blank">
                  <span className="i-ph:globe" />
                  {url}
                </Link>
              </div>

              <div className="mt-4 flex gap-2">
                <Button
                  variant="primary"
                  size="md"
                  onClick={() => {
                    handleDomainAction('buy');
                  }}
                >
                  Buy a new domain
                </Button>
                <Button
                  variant="secondary"
                  size="md"
                  onClick={() => {
                    handleDomainAction('connect');
                  }}
                >
                  Connect a domain you own
                </Button>
              </div>
            </>
          }
        />
        <SectionRow
          layout="vertical"
          title="Hosting settings"
          innerClassName="flex flex-col gap-4"
          titleClassName="text-bolt-ds-textTertiary font-semibold mb-0"
          action={<UnpublishProject url={url} projectId={projectId} />}
        />
        <UpgradePlanDialogs
          isOpen={isUpgradeModalOpen}
          cards={[!activeOrganization ? 'pro' : 'teams']}
          onClose={() => setIsUpgradeModalOpen(false)}
          message={`Attaching custom domains to your site is a ${!activeOrganization ? 'Pro' : 'Teams'} feature, upgrade to ${!activeOrganization ? 'Pro' : 'Teams'} to unlock it.`}
        />
      </>
    );
  },
);

ProjectPublishedOnSubdomain.displayName = 'ProjectPublishedOnSubdomain';
